import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import './SearchableDropdown.css';

const SearchableDropdown = ({ 
  options = [], 
  value = '', 
  onChange, 
  placeholder = 'Search and select...', 
  allowCustom = false,
  disabled = false,
  required = false,
  name = '',
  id = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Update filtered options when search term or options change
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option =>
        option.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
    setHighlightedIndex(-1);
  }, [searchTerm, options]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleOptionSelect(filteredOptions[highlightedIndex]);
        } else if (allowCustom && searchTerm.trim()) {
          handleOptionSelect(searchTerm.trim());
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        inputRef.current?.blur();
        break;
      case 'Tab':
        setIsOpen(false);
        setSearchTerm('');
        break;
      default:
        break;
    }
  };

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex]);

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    
    if (allowCustom) {
      onChange(newValue);
    }
    
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleOptionSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
    inputRef.current?.blur();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const displayValue = isOpen ? searchTerm : value;

  return (
    <div 
      className={`searchable-dropdown ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
      ref={dropdownRef}
    >
      <div className="dropdown-input-container">
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          name={name}
          id={id}
          className="dropdown-input"
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
        />
        
        <div className="dropdown-icons">
          {value && !disabled && (
            <button
              type="button"
              className="clear-button"
              onClick={handleClear}
              tabIndex={-1}
              aria-label="Clear selection"
            >
              <X size={14} />
            </button>
          )}
          <div className={`dropdown-arrow ${isOpen ? 'rotated' : ''}`}>
            <ChevronDown size={16} />
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="dropdown-menu" role="listbox">
          {filteredOptions.length > 0 ? (
            <ul ref={listRef} className="dropdown-list">
              {filteredOptions.map((option, index) => (
                <li
                  key={option}
                  className={`dropdown-option ${index === highlightedIndex ? 'highlighted' : ''} ${value === option ? 'selected' : ''}`}
                  onClick={() => handleOptionSelect(option)}
                  role="option"
                  aria-selected={value === option}
                >
                  {option}
                </li>
              ))}
            </ul>
          ) : (
            <div className="dropdown-no-options">
              {allowCustom && searchTerm.trim() ? (
                <div 
                  className="dropdown-option custom-option"
                  onClick={() => handleOptionSelect(searchTerm.trim())}
                >
                  <Search size={14} />
                  Add "{searchTerm.trim()}"
                </div>
              ) : (
                <div className="no-results">
                  No options found
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
